import React, { useState } from 'react';

import { Button, Empty, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';

import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { api } from '../../../http/API';
import { FieldCropHistory } from '../../../models/FieldCropHistory';
import { Culture } from '../../../models/Culture';

import FieldSeasonModal from '../components/FieldSeasonModal';

const { Text } = Typography;

interface Props {
    fieldId: number;
    seasons: FieldCropHistory[];
    cultures: Culture[];
    onReload: () => void;
}

const STATUS_LABELS: Record<string, string> = {
    planned: 'Запланирован',
    sown: 'Посеян',
    harvested: 'Убран',
};

const FieldSeasonsTab = (props: Props) => {
    const { fieldId, seasons, cultures, onReload } = props;
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [editing, setEditing] = useState<FieldCropHistory | null>(null);

    const cultureName = (id: number) => {
        const c = cultures.find((x) => x.id === id);
        return c ? c.name : `#${id}`;
    };

    const handleCreate = () => {
        setEditing(null);
        setModalOpen(true);
    };

    const handleEdit = (row: FieldCropHistory) => {
        setEditing(row);
        setModalOpen(true);
    };

    const handleDelete = (row: FieldCropHistory) => {
        api.DeleteFieldSeason(fieldId, row.season_harvest)
            .then(() => {
                message.success('Сезон удалён');
                onReload();
            })
            .catch(() => message.error('Не удалось удалить сезон'));
    };

    const columns = [
        { title: 'Год уборки', dataIndex: 'season_harvest', width: 120 },
        { title: 'Год посева', dataIndex: 'season_sowing', width: 120 },
        {
            title: 'Культура',
            dataIndex: 'culture_id',
            render: (value: number) => cultureName(value),
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            width: 140,
            render: (value: string) => <Tag>{STATUS_LABELS[value] || value}</Tag>,
        },
        {
            title: 'Дата посева',
            dataIndex: 'sowing_date',
            width: 130,
            render: (v: string | null) => v || '—',
        },
        {
            title: 'Дата уборки',
            dataIndex: 'harvest_date',
            width: 130,
            render: (v: string | null) => v || '—',
        },
        {
            title: 'Урожайность',
            dataIndex: 'actual_yield',
            width: 120,
            render: (v: number | null) => (v != null ? `${v} ц/га` : '—'),
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 160,
            render: (_: any, row: FieldCropHistory) => (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(row)}>
                        Изменить
                    </Button>
                    <Popconfirm
                        title={`Удалить сезон ${row.season_harvest}?`}
                        okText="Да"
                        cancelText="Нет"
                        onConfirm={() => handleDelete(row)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />}>
                            Удалить
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
                style={{ marginBottom: 16 }}
            >
                Добавить сезон
            </Button>

            {seasons.length === 0 ? (
                <Empty description="Сезонов пока нет" />
            ) : (
                <Table
                    rowKey="season_harvest"
                    dataSource={seasons}
                    columns={columns}
                    pagination={false}
                    size="small"
                />
            )}

            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Одна запись = одно поле × один сезон × одна культура.
            </Text>

            <FieldSeasonModal
                open={modalOpen}
                fieldId={fieldId}
                season={editing}
                onCancel={() => setModalOpen(false)}
                onSaved={() => {
                    setModalOpen(false);
                    onReload();
                }}
            />
        </div>
    );
};

export default FieldSeasonsTab;
